import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, ViewEncapsulation } from '@angular/core';

import {
  ChipActionBranding, ChipActionSizeConfig, ChipActionSizeConfigProps, ChipActionSizes, ChipActionStatus, ChipActionType
} from '@design-system/components/molecules/chip-action/entities/chip-action.types';
import { TypographyFontSize, TypographyFontFamily } from '@design-system/components/atoms/typography/entities/typography.types';
import { LoaderRingType } from '@design-system/components/atoms/loader-ring/entities/loader-ring.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';
import { LoaderRingDsComponent } from '@design-system/components/atoms/loader-ring/loader-ring.component';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';

@Component({
  selector: 'app-ds-chip-action',
  templateUrl: './chip-action.component.html',
  styleUrls: ['./chip-action.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    IconDsComponent,
    LoaderRingDsComponent,
    TypographyDsComponent
  ]
})
export class ChipActionDsComponent {

  @Input() chipStatus = ChipActionStatus.Default;
  @Input() size = ChipActionSizes.MD;
  @Input() type = ChipActionType.Compact;
  @Input() branding = ChipActionBranding.None;
  @Input() isDisabled = false;
  @Input() isLoading = false;
  @Input() icon: Icon;
  @Input() label: string;

  @Output() clickEvent: EventEmitter<void> = new EventEmitter<void>();

  constructor() { }

  get chipActionBranding(): ChipActionBranding {
    return this.branding ?? ChipActionBranding.None;
  }

  get chipActionConfig(): ChipActionSizeConfigProps {
    return ChipActionSizeConfig[this.size] ?? null;
  }

  get fontFamily(): TypographyFontFamily {
    return this.chipActionConfig?.[this.type]?.FontFamily ?? null;
  }

  get iconSize(): number {
    const size = this.chipActionConfig?.[this.type]?.IconSize ?? null;
    return this.icon === Icon.Civitatis ? (size ? (size * 2) : null) : size;
  }

  get fontSize(): TypographyFontSize {
    return this.chipActionConfig?.[this.type]?.FontSize ?? null;
  }

  get loaderSize(): string {
    return this.chipActionConfig?.[this.type]?.LoaderSize ?? null;
  }

  get height(): string {
    return this.chipActionConfig?.[this.type]?.Height ?? null;
  }

  get width(): string {
    return this.type === ChipActionType.Compact ? (this.chipActionConfig?.[this.type]?.Width ?? null) : null;
  }

  get loaderRingType(): LoaderRingType {
    return this.isDisabled && this.isLoading ? LoaderRingType.Neutral : LoaderRingType.Secondary;
  }

  get showLabel(): boolean {
    return this.type === ChipActionType.Compact ? !!this.label : !!this.label && !this.isLoading;
  }

  get isValidBranding() {
    return this.type === ChipActionType.Long ?
      this.chipActionBranding === ChipActionBranding.Civitatis && !this.isLoading :
      this.chipActionBranding === ChipActionBranding.Civitatis;
  }

  get iconBranding(): Icon {
    return this.chipActionBranding === ChipActionBranding.Civitatis ? Icon.Civitatis : null;
  }

  get iconBrandingSize(): number {
    return this.type === ChipActionType.Long ? 28 : 32;
  }

  public clickTrigger(): void {
    if (!this.isDisabled && !this.isLoading) {
      this.clickEvent.emit();
    }
  }

}
